import { isArray, isUndefined } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { toArray } from '@flatbiz/utils';
import { Children, CSSProperties, useMemo } from 'react';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { px2rem } from '../utils/pxrem';
import { isStyleNameSupport } from '../utils/style-checker';
import './style.less';

export type SpaceProps = {
  /**
   * 间距配置
   * 1.单数据配置：上下、左右设置相同
   * 2.数组配置：[上下, 左右]
   */
  gap?: number | [number, number];
  align?: CSSProperties['alignItems'];
  // 是否自动换行
  wrap?: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  // 是否width：100%
  block?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  /**
   * 布局方向
   */
  direction?: 'vertical' | 'horizontal';
  flexFullIndex?: number | number[];
} & CommonPropsWithChildren<{ '--space-display': 'flex' | 'inline-flex' }>;

/**
 * 间距，设置组件之间的间距。
 * ```
 * 1. wrap是否还款，默认值：true
 * 2. block是否width：100%
 * ```
 */
export const Space = (props: SpaceProps) => {
  const supportFlexGap = isStyleNameSupport('gap');
  // const supportFlexGap = false;
  const align = (props.align || 'center') as string;
  const direction = props.direction || 'horizontal';
  const flexFullIndexList = toArray<number>(props.flexFullIndex);
  const gap = isUndefined(props.gap) ? 30 : props.gap;
  const gapArray = (isArray(gap) ? gap : [gap]) as number[];
  const verticalSize = px2rem(gapArray[0]);
  const horizontalSize = px2rem(gapArray[1] || gapArray[0]);
  const verticalHalfSize = `${parseFloat(verticalSize) / 2}rem`;
  const horizontalHalfSize = `${parseFloat(horizontalSize) / 2}rem`;

  const wrapperStyle = useMemo(() => {
    const style: CSSProperties = {};
    if (supportFlexGap) {
      style['--space-gap'] = `${verticalSize} ${horizontalSize}`;
    } else {
      style['--space-margin'] = `-${verticalHalfSize} -${horizontalHalfSize}`;
    }

    if (props.justifyContent) {
      style['--space-justify-content'] = props.justifyContent;
    }

    if (align) {
      style['--space-align-items'] = align;
    }
    return style;
  }, [
    align,
    horizontalHalfSize,
    horizontalSize,
    props.justifyContent,
    supportFlexGap,
    verticalHalfSize,
    verticalSize,
  ]);

  const childrenList = Children.toArray(props.children);

  const className = classNames('v-space', `v-space-${direction}`, {
    'v-space-wrap': props.wrap,
    'v-space-block': props.block,
    'v-space-not-support-flex-gap': !supportFlexGap,
  });

  return withNativeProps(
    props,
    <div onClick={props.onClick} className={className} style={wrapperStyle}>
      {childrenList.map((item, index) => {
        const childrenStyle: CSSProperties = {};
        if (props.direction === 'vertical') {
          childrenStyle.width = '100%';
        }
        if (!supportFlexGap) {
          childrenStyle['--space-item-padding'] = `${verticalHalfSize} ${horizontalHalfSize}`;
        }

        if (flexFullIndexList.includes(index)) {
          childrenStyle.flex = 1;
        }

        return (
          <div className="v-space-item" style={childrenStyle} key={index}>
            {item}
          </div>
        );
      })}
    </div>,
  );
};
